import type { ApiFailure, AuthSession, ErrorCode, Page } from '@blackblack/shared';

/**
 * Typed API client. Handles the response envelope, access-token injection and
 * transparent refresh (single-flight) when the access token expires.
 */
export const API_BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'NETWORK',
    message: string,
    readonly details?: Record<string, string>,
  ) {
    super(message);
  }
}

type SessionListener = (s: AuthSession | null) => void;

let accessToken: string | null = null;
let refreshing: Promise<AuthSession | null> | null = null;
let onSession: SessionListener = () => {};

export function setAccessToken(token: string | null) {
  accessToken = token;
}
export function onSessionChange(l: SessionListener) {
  onSession = l;
}

async function parse<T>(res: Response): Promise<{ data: T; meta?: { nextCursor: string | null } }> {
  if (res.status === 204) return { data: undefined as T };
  const body = (await res.json().catch(() => null)) as ({ data: T; meta?: { nextCursor: string | null } } & Partial<ApiFailure>) | null;
  if (!res.ok || !body || body.error) {
    const e = body?.error;
    throw new ApiError(res.status, e?.code ?? 'INTERNAL', e?.message ?? 'Er ging iets mis', e?.details);
  }
  return body;
}

/** Exchanges the httpOnly refresh cookie for a new session. */
export function refreshSession(): Promise<AuthSession | null> {
  refreshing ??= fetch(`${API_BASE}/auth/refresh`, { method: 'POST', credentials: 'same-origin' })
    .then((r) => (r.ok ? parse<AuthSession>(r).then((b) => b.data) : null))
    .catch(() => null)
    .then((s) => {
      accessToken = s?.accessToken ?? null;
      onSession(s);
      return s;
    })
    .finally(() => (refreshing = null));
  return refreshing;
}

interface RequestOpts {
  query?: Record<string, string | number | boolean | null | undefined>;
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(method: string, path: string, opts: RequestOpts = {}, retried = false): Promise<{ data: T; meta?: { nextCursor: string | null } }> {
  const qs = opts.query
    ? '?' + new URLSearchParams(Object.entries(opts.query).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => [k, String(v)])).toString()
    : '';
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}${qs}`, {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : null,
      credentials: 'same-origin',
      signal: opts.signal ?? null,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(0, 'NETWORK', 'Geen verbinding. Controleer je internet.');
  }
  if (res.status === 401 && accessToken && !retried) {
    const s = await refreshSession();
    if (s) return request<T>(method, path, opts, true);
  }
  return parse<T>(res);
}

export const api = {
  get: <T>(path: string, query?: RequestOpts['query'], signal?: AbortSignal) => request<T>('GET', path, { query, signal }).then((r) => r.data),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body: body ?? {} }).then((r) => r.data),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body: body ?? {} }).then((r) => r.data),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body: body ?? {} }).then((r) => r.data),
  delete: <T>(path: string) => request<T>('DELETE', path).then((r) => r.data),
  page: async <T>(path: string, query?: RequestOpts['query'], signal?: AbortSignal): Promise<Page<T>> => {
    const r = await request<T[]>('GET', path, { query, signal });
    return { items: r.data, nextCursor: r.meta?.nextCursor ?? null };
  },
};

/* ---------- uploads (two-step: intent → PUT bytes) ---------- */

export interface UploadResult {
  uploadId: string;
  kind: 'video' | 'image' | 'audio';
  mime: string;
  sizeBytes: number;
}

export async function uploadFile(file: Blob, kind: 'video' | 'image' | 'audio', onProgress?: (fraction: number) => void): Promise<UploadResult> {
  const contentType = file.type || 'application/octet-stream';
  const intent = await api.post<{ uploadId: string; uploadUrl: string; headers: Record<string, string>; direct: boolean; completeUrl: string | null }>('/uploads', {
    kind,
    sizeBytes: file.size,
    contentType,
  });

  const put = (url: string, headers: Record<string, string>) =>
    new Promise<{ status: number; text: string }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);
      for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
      xhr.onerror = () => reject(new ApiError(0, 'NETWORK', 'Upload mislukt — controleer je verbinding'));
      xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText });
      xhr.send(file);
    });

  // Direct mode: bytes go straight to object storage (R2) via a presigned URL; the API never sees them.
  if (intent.direct && intent.completeUrl) {
    const r = await put(intent.uploadUrl, intent.headers);
    if (r.status < 200 || r.status >= 300) throw new ApiError(r.status, 'INTERNAL', 'Upload naar opslag mislukt');
    return api.post<UploadResult>(intent.completeUrl.replace(API_BASE, ''));
  }

  // Proxy mode: the API receives the bytes.
  const viaApi = async () => {
    const r = await put(intent.uploadUrl, { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}), 'Content-Type': contentType });
    const body = JSON.parse(r.text || '{}') as { data?: UploadResult } & Partial<ApiFailure>;
    if (r.status >= 200 && r.status < 300 && body.data) return body.data;
    throw new ApiError(r.status, body.error?.code ?? 'INTERNAL', body.error?.message ?? 'Upload mislukt');
  };
  try {
    return await viaApi();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401 && (await refreshSession())) return viaApi();
    throw err;
  }
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return 'Er ging iets mis';
}
